import { useCallback } from 'react'
import { useAppSelector } from '../../../store/hooks'
import {
  selectHistorySyncStatus,
  selectHistoryContactValues,
  selectHistoryContactSchema,
  selectHistoryPreviousContactValues,
} from '../../../store/meetingSummary/contactUpdate'
import { escapeHtml, formatDate } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { getIntegrationDisplayName } from '../../../common/integrations'
import { useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { TrackEvent } from '../../../apps/professional-app/fa/common/tracking'
import { extendCopyContentWithComplianceMessage, HTML_TYPE, TEXT_TYPE, writeCopyContentToClipboard } from '../../../common/copy'
import { useEffectiveConfig } from '../../../common/useEffectiveConfig'
import { DEFAULT_SEARCH_KEY, getPreviousValue, SEARCH_KEY_MAP } from '../utils'

export const PREVIOUS_VALUE_NOT_SET_LABEL = 'Not set'

const isNameField = (fieldName: string): boolean => {
  const lowerFieldName = fieldName.toLowerCase()
  return lowerFieldName === 'name' || lowerFieldName === 'title' || lowerFieldName === 'description'
}

const getCategoryTitle = (categoryKey: string, schema: Record<string, unknown> | null): string | null => {
  if (!categoryKey || !schema) {
    return null
  }

  try {
    if (schema.properties && typeof schema.properties === 'object') {
      const properties = schema.properties as Record<string, unknown>
      const categorySchema = properties[categoryKey]

      if (categorySchema && typeof categorySchema === 'object') {
        const schemaObj = categorySchema as Record<string, unknown>
        if (schemaObj.title && typeof schemaObj.title === 'string') {
          return schemaObj.title
        }
      }
    }
  } catch {
    return null
  }

  return null
}

const toTitleCase = (fieldName: string): string => {
  return (
    fieldName.charAt(0).toUpperCase() +
    fieldName
      .slice(1)
      .replace(/([A-Z])/g, ' $1')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .trim()
  )
}

const getFieldSchema = (fieldPath: string[], schema: Record<string, unknown> | null): Record<string, unknown> | null => {
  if (!fieldPath || fieldPath.length === 0 || !schema) {
    return null
  }

  try {
    return fieldPath.reduce<Record<string, unknown> | null>((currentSchema, fieldName) => {
      if (!fieldName || typeof fieldName !== 'string' || !currentSchema || typeof currentSchema !== 'object') {
        return null
      }

      const schemaObj = currentSchema as Record<string, unknown>

      if (schemaObj.properties && typeof schemaObj.properties === 'object') {
        const properties = schemaObj.properties as Record<string, unknown>
        if (properties[fieldName] && typeof properties[fieldName] === 'object') {
          return properties[fieldName] as Record<string, unknown>
        }
      }

      if (schemaObj.items && typeof schemaObj.items === 'object') {
        const items = schemaObj.items as Record<string, unknown>
        if (items.properties && typeof items.properties === 'object') {
          const itemProperties = items.properties as Record<string, unknown>
          if (itemProperties[fieldName] && typeof itemProperties[fieldName] === 'object') {
            return itemProperties[fieldName] as Record<string, unknown>
          }
        }
      }

      return null
    }, schema)
  } catch {
    return null
  }
}

const getFieldTitle = (fieldPath: string[], schema: Record<string, unknown> | null): string => {
  if (!fieldPath || fieldPath.length === 0) {
    return 'Field'
  }

  const lastField = fieldPath[fieldPath.length - 1]

  if (!schema) {
    return toTitleCase(lastField)
  }

  try {
    const fieldSchema = getFieldSchema(fieldPath, schema)
    if (fieldSchema?.title && typeof fieldSchema.title === 'string') {
      return fieldSchema.title
    }

    if (fieldPath.length === 2) {
      const [categoryKey, subcategoryKey] = fieldPath

      if (schema[categoryKey] && typeof schema[categoryKey] === 'object') {
        const categorySchema = schema[categoryKey] as Record<string, unknown>

        if (categorySchema.properties && typeof categorySchema.properties === 'object') {
          const properties = categorySchema.properties as Record<string, unknown>
          const subcategorySchema = properties[subcategoryKey]

          if (subcategorySchema && typeof subcategorySchema === 'object') {
            const subSchemaObj = subcategorySchema as Record<string, unknown>
            if (subSchemaObj.title && typeof subSchemaObj.title === 'string') {
              return subSchemaObj.title
            }
          }
        }
      }
    }
  } catch {
    return toTitleCase(lastField)
  }
  return toTitleCase(lastField)
}

interface SimpleField {
  name: string
  value: string
}

interface Group {
  title?: string
  header?: SimpleField
  fields: SimpleField[]
  subcategories: Group[]
  separateWithBlankLine: boolean
}

interface FormatContactDataResult {
  text: string
  html: string
}

const includeField = (value: unknown): boolean => {
  return value !== null && value !== undefined && value !== ''
}

const formatValue = (value: unknown): string => {
  if (typeof value === 'number') {
    if (value >= 1000) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)
    }
    return value.toString()
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  if (typeof value === 'object' && value !== null) {
    return 'Not specified'
  }
  return String(value)
}

const extractGroups = (obj: unknown, schema: Record<string, unknown> | null, path: string[] = []): Group[] => {
  if (typeof obj !== 'object' || obj === null) {
    return []
  }

  if (Array.isArray(obj)) {
    return extractArrayGroups(obj, schema, path)
  }

  return extractObjectGroups(obj as Record<string, unknown>, schema, path)
}

interface ArrayItemAccumulator {
  headerField?: SimpleField
  fields: SimpleField[]
  subcategories: Group[]
}

const getArrayItemHeaderKey = (item: Record<string, unknown>): string | undefined =>
  Object.keys(item).find((key) => isNameField(key) && includeField(item[key]) && typeof item[key] !== 'object')

const extractArrayGroups = (arr: unknown[], schema: Record<string, unknown> | null, path: string[]): Group[] => {
  const groups: Group[] = []

  arr.forEach((item) => {
    if (typeof item !== 'object' || item === null) {
      return
    }

    const itemRecord = item as Record<string, unknown>
    const headerKey = getArrayItemHeaderKey(itemRecord)

    const result = Object.entries(itemRecord).reduce<ArrayItemAccumulator>(
      (acc, [key, value]) => {
        if (!includeField(value)) {
          return acc
        }

        if (typeof value === 'object' && value !== null) {
          const nested = extractGroups(value, schema, [...path, key])
          if (nested.length > 0) {
            acc.subcategories.push({
              title: getFieldTitle([...path, key], schema),
              fields: [],
              subcategories: nested,
              separateWithBlankLine: false,
            })
          }
        } else {
          const formattedValue = formatValue(value)
          if (formattedValue === 'Not specified') {
            return acc
          }

          const fieldName = getFieldTitle([...path, key], schema)
          const field: SimpleField = { name: fieldName, value: formattedValue }

          if (key === headerKey) {
            return { ...acc, headerField: field }
          }
          acc.fields.push(field)
        }
        return acc
      },
      { headerField: undefined, fields: [], subcategories: [] },
    )

    if (result.fields.length > 0 || result.subcategories.length > 0) {
      groups.push({
        header: result.headerField,
        fields: result.fields,
        subcategories: result.subcategories,
        separateWithBlankLine: true,
      })
    }
  })

  return groups
}

const extractObjectGroups = (obj: Record<string, unknown>, schema: Record<string, unknown> | null, path: string[]): Group[] => {
  const fields: SimpleField[] = []
  const subcategories: Group[] = []

  Object.entries(obj).forEach(([key, value]) => {
    if (!includeField(value)) {
      return
    }

    if (typeof value === 'object' && value !== null) {
      const nested = extractGroups(value, schema, [...path, key])
      if (nested.length > 0) {
        subcategories.push({
          title: getFieldTitle([...path, key], schema),
          fields: [],
          subcategories: nested,
          separateWithBlankLine: true,
        })
      }
    } else {
      const formattedValue = formatValue(value)
      if (formattedValue === 'Not specified') {
        return
      }

      fields.push({
        name: getFieldTitle([...path, key], schema),
        value: formattedValue,
      })
    }
  })

  if (fields.length === 0 && subcategories.length === 0) {
    return []
  }

  return [{ fields, subcategories, separateWithBlankLine: true }]
}

const renderGroup = (group: Group, content: FormatContactDataResult, indentLevel: number) => {
  const indent = '  '.repeat(indentLevel)

  if (group.title) {
    content.text += `${indent}${group.title}:\n`
    content.html += `<li><strong>${escapeHtml(group.title)}:</strong>`
  }

  if (group.header) {
    const headerIndent = '  '.repeat(indentLevel + (group.title ? 1 : 0))
    content.text += `${headerIndent}${group.header.value}:\n`
    content.html += `<li><strong>${escapeHtml(group.header.value)}:</strong>`
  }

  const hasContent = group.fields.length > 0 || group.subcategories.length > 0
  const fieldIndentLevel = indentLevel + (group.title ? 1 : 0) + (group.header ? 1 : 0)
  const fieldIndent = '  '.repeat(fieldIndentLevel)

  if (hasContent) {
    content.html += '<ul>'
  }

  if (group.fields.length > 0) {
    group.fields.forEach((field) => {
      content.text += `${fieldIndent}• ${field.name}: ${field.value}\n`
      content.html += `<li><strong>${escapeHtml(field.name)}:</strong> ${escapeHtml(field.value)}</li>`
    })
  }

  const subIndentLevel = indentLevel + (group.title ? 1 : 0) + (group.header ? 1 : 0)

  if (group.subcategories.length > 0) {
    renderGroups(group.subcategories, content, subIndentLevel)
  }

  if (hasContent) {
    content.html += '</ul>'
  }

  if (group.title || group.header) {
    content.html += '</li>'
  }
}

const renderGroups = (groups: Group[], content: FormatContactDataResult, indentLevel: number) => {
  groups.forEach((group, index) => {
    if (index > 0 && group.separateWithBlankLine) {
      content.text += '\n'
    }
    renderGroup(group, content, indentLevel)
  })
}

const formatContactData = (data: Record<string, unknown>, schema: Record<string, unknown> | null): FormatContactDataResult => {
  const content: FormatContactDataResult = {
    text: '',
    html: '',
  }

  Object.entries(data).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return
    }
    if (typeof value === 'object' && Object.keys(value as object).length === 0) {
      return
    }

    const categoryName = getCategoryTitle(key, schema) || toTitleCase(key)
    const groups = extractGroups(value, schema, [key])
    if (groups.length === 0) {
      return
    }

    content.text += `\n${categoryName}:\n`
    content.html += `<h3>${escapeHtml(categoryName)}</h3><ul>`

    renderGroups(groups, content, 1)

    content.html += '</ul>'
  })

  return { text: content.text.trim(), html: content.html }
}

const toPreviousValues = (value: unknown, path: string[], getPrevious: (path: string[]) => unknown, isArrayItem = false): unknown => {
  if (Array.isArray(value)) {
    return value.map((item, index) => toPreviousValues(item, [...path, String(index)], getPrevious, true))
  }

  if (typeof value === 'object' && value !== null) {
    const record = value as Record<string, unknown>
    const headerKey = isArrayItem ? getArrayItemHeaderKey(record) : undefined
    return Object.fromEntries(
      Object.entries(record).map(([key, item]) => [key, key === headerKey ? item : toPreviousValues(item, [...path, key], getPrevious)]),
    )
  }

  if (!includeField(value)) {
    return value
  }

  const previousValue = getPrevious(path)
  return includeField(previousValue) ? previousValue : PREVIOUS_VALUE_NOT_SET_LABEL
}

export const formatContactHistory = (
  contactValues: Record<string, unknown>,
  previousContactValues: Record<string, unknown> | null,
  schema: Record<string, unknown> | null,
  searchKey: string,
): FormatContactDataResult => {
  const savedData = formatContactData(contactValues, schema)
  if (!previousContactValues) {
    return savedData
  }

  const getPrevious = (path: string[]) => getPreviousValue(contactValues, previousContactValues, path, searchKey)
  const previousValues = Object.fromEntries(Object.entries(contactValues).map(([key, value]) => [key, toPreviousValues(value, [key], getPrevious)]))
  const previousData = formatContactData(previousValues, schema)

  return {
    text: `New values:\n${savedData.text}\n\nPrevious values:\n${previousData.text}`,
    html: `<p><strong>New values:</strong></p>${savedData.html}<p><strong>Previous values:</strong></p>${previousData.html}`,
  }
}

export const useCopyContactHistory = () => {
  const historySyncStatus = useAppSelector(selectHistorySyncStatus)
  const historyContactValues = useAppSelector(selectHistoryContactValues)
  const historyContactSchema = useAppSelector(selectHistoryContactSchema)
  const historyPreviousContactValues = useAppSelector(selectHistoryPreviousContactValues)

  const { trackingClient } = useTracking()
  const { copyComplianceMessage, accountIsFetching } = useEffectiveConfig()

  const handleCopyAll = useCallback(async () => {
    if (!historySyncStatus || !historyContactValues || accountIsFetching) {
      return
    }

    const contactName = historySyncStatus.name || 'Contact'
    const contactObjectType = historySyncStatus.externalContactObjectType || 'contact'
    const serviceName = getIntegrationDisplayName(historySyncStatus.serviceId)
    const formattedDate = formatDate(historySyncStatus.syncedAt)

    const headerText = `'${contactName}' ${contactObjectType} saved in ${serviceName}\nDate: ${formattedDate}`
    const headerHtml = `<h2>'${escapeHtml(contactName)}' ${escapeHtml(contactObjectType)} saved in ${escapeHtml(serviceName)}</h2><p><strong>Date:</strong> ${escapeHtml(formattedDate)}</p>`

    const searchKey = SEARCH_KEY_MAP[historySyncStatus.serviceId] ?? DEFAULT_SEARCH_KEY
    const contactData = formatContactHistory(historyContactValues, historyPreviousContactValues, historyContactSchema, searchKey)

    const textContent = headerText + '\n\n' + contactData.text
    const htmlContent = headerHtml + contactData.html

    const _copyContent = { [TEXT_TYPE]: textContent, [HTML_TYPE]: htmlContent }
    const copyContent = extendCopyContentWithComplianceMessage(_copyContent, copyComplianceMessage)

    await writeCopyContentToClipboard(copyContent)

    trackingClient.track({
      eventName: TrackEvent.CONTACT_UPDATE_CHANGELOG_COPIED,
      eventDetails: {
        serviceId: historySyncStatus.serviceId,
      },
    })
  }, [historySyncStatus, historyContactValues, historyPreviousContactValues, accountIsFetching, historyContactSchema, copyComplianceMessage, trackingClient])

  return {
    handleCopyAll,
    isCopyEnabled: !!(historySyncStatus && historyContactValues && !accountIsFetching),
  }
}
